import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@rover/kibble/core';
import type { ApiLocationGeocodeRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiLocationGeocodeRetrieve200';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import ClearButton from './components/ClearButton';
import InputBody from './components/InputBody';
import LocationErrorModal from './components/LocationErrorModal';
import LocationSuggestions from './components/LocationSuggestions';
import useHomeAddress from './hooks/useHomeAddress';
import useInputActiveIndex from './hooks/useInputActiveIndex';
import useInputValue from './hooks/useInputValue';
import type { SelectOption } from './LocationInput.types';
import { convertToLocationInputFormat, shouldShowCurrentLocation } from './utils';

export type Props = {
  className?: string;
  inputHeight?: string;
  inputFontSize?: string;
  clearOnFocus?: boolean;
  disabled?: boolean;
  inputAriaLabel?: string;
  dismissErrorMessage: (arg0: { inputId: string }) => void;
  clearOptions: (arg0: { inputId: string }) => void;
  errorMessage?: string;
  floatingMenu?: boolean;
  inputId: string;
  inputValue?: string;
  countryCode: string | null | undefined;
  loading?: boolean;
  loadOptions?: (arg0: {
    query: string;
    inputId: string;
    countryCode: string | null | undefined;
  }) => void;
  onBlur?: () => void;
  onClear?: () => void;
  onChange: (
    arg0: {
      label?: string | null;
      lat?: string | null;
      lng?: string | null;
      locationAccuracy?: string | null;
      locationType?: string | null;
      rawLocationTypes?: string[] | null;
      value?: string | null;
      placeId?: string | null;
      addressLine1?: string | null;
      city?: string | null;
      state?: string | null;
      postalCode?: string | null;
      countryCode?: string | null;
    } | null
  ) => void;
  onFocus?: () => void;
  onInputChange?: (arg0: string) => void;
  options: SelectOption[];
  renderIcon?: (arg0: { focused: boolean }) => ReactNode;
  controlledInputValue?: string;
  renderIconAlignment?: 'left' | 'right';
  renderIconAsPlaceholder?: boolean;
  suggestionsMenuAlignment?: 'left' | 'right';
  placeholder?: string;
  placeholderColor?: string;
  channel?: string;
  validationType: 'popover' | 'inline';
  validationError?: string | null | undefined;
  includeHomeAddress?: boolean;
  forceUseHomeAddress?: boolean;
  enableTwoLineSuggestions?: boolean;
};

const LocationInput: FC<Props> = (props) => {
  const {
    className,
    inputValue: propInputValue,
    disabled,
    dismissErrorMessage,
    clearOptions,
    errorMessage,
    floatingMenu = true,
    inputId,
    loading,
    loadOptions,
    options,
    renderIcon,
    onClear,
    onBlur,
    onChange,
    onInputChange,
    controlledInputValue,
    renderIconAlignment = 'left',
    renderIconAsPlaceholder = false,
    suggestionsMenuAlignment,
    inputHeight,
    inputFontSize,
    inputAriaLabel,
    validationType = 'popover',
    validationError,
    placeholder,
    placeholderColor,
    clearOnFocus,
    countryCode,
    includeHomeAddress = true,
    forceUseHomeAddress = false,
    enableTwoLineSuggestions = false,
  } = props;

  const [inputFocused, setInputFocused] = useState(false);
  const { activeIndex, setActiveIndex } = useInputActiveIndex(options, inputFocused);
  const { formattedAddress, enabled: homeAddressEnabled } = useHomeAddress(forceUseHomeAddress);

  // Compute initial input strictly from prop or home address
  const initialInputValue = propInputValue ?? formattedAddress ?? '';

  const { inputValue, setInputValue } = useInputValue(
    initialInputValue,
    errorMessage,
    clearOnFocus,
    inputFocused
  );

  useEffect(() => {
    if (propInputValue !== undefined) {
      setInputValue(propInputValue);
    }
  }, [propInputValue, setInputValue]);

  useEffect(() => {
    if (controlledInputValue !== undefined) {
      setInputValue(controlledInputValue);
    }
  }, [controlledInputValue, setInputValue]);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const showSuggestions = useMemo((): boolean => {
    if (homeAddressEnabled) {
      return inputFocused;
    }

    if (shouldShowCurrentLocation()) {
      return Boolean(inputFocused && options.length);
    }

    return Boolean(inputFocused && inputValue && options.length);
  }, [inputFocused, options, inputValue, homeAddressEnabled]);

  const handleSelectChange = useCallback(
    (
      selectedOption: SelectOption,
      fullAddressData?: ApiLocationGeocodeRetrieve200 | UserInformationAddress
    ) => {
      // If full address data is provided, convert and pass it to onChange; otherwise pass the SelectOption
      if (fullAddressData) {
        const converted = convertToLocationInputFormat(fullAddressData);
        onChange(converted);
      } else {
        onChange(selectedOption);
      }
      setInputValue(selectedOption.label ?? '');
      setInputFocused(false);
      inputRef.current?.blur();
      clearOptions({ inputId });
    },
    [onChange, setInputValue, clearOptions, inputId]
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    onChange({});
    onClear?.();
    clearOptions({ inputId });
    inputRef.current?.focus();
  }, [onChange, onClear, setInputValue, clearOptions, inputId]);

  const iconTop = inputRef.current ? `${Math.round(inputRef.current.offsetHeight / 2)}px` : '50%';

  return (
    <>
      {validationType === 'popover' && (
        <LocationErrorModal
          data-qa-id="error-modal"
          onRequestClose={() => {
            dismissErrorMessage({
              inputId,
            });
          }}
          message={errorMessage || ''}
        />
      )}
      <Box
        position="relative"
        width="100%"
        className={className}
        sx={{
          fontSize: inputFontSize ?? 'inherit',
        }}
      >
        <InputBody
          inputId={inputId}
          inputValue={inputValue}
          countryCode={countryCode}
          inputRef={inputRef}
          inputFocused={inputFocused}
          inputAriaLabel={inputAriaLabel}
          inputFontSize={inputFontSize}
          inputHeight={inputHeight}
          placeholder={placeholder}
          placeholderColor={placeholderColor}
          loading={loading}
          hasIcon={Boolean(renderIcon)}
          renderIconAlignment={renderIconAlignment}
          floatingMenu={!!floatingMenu}
          showSuggestions={showSuggestions}
          disabled={disabled}
          validationType={validationType}
          validationError={validationError}
          options={options}
          activeIndex={activeIndex !== null ? activeIndex : undefined}
          setActiveIndex={setActiveIndex}
          handleSelectChange={handleSelectChange}
          onBlur={onBlur}
          onInputChange={onInputChange}
          loadOptions={loadOptions}
          setInputValue={setInputValue}
          setInputFocused={setInputFocused}
        />

        {renderIcon &&
          (renderIconAlignment === 'left' || !inputFocused) &&
          !((inputValue && renderIconAsPlaceholder) || (renderIconAsPlaceholder && loading)) && (
            <Box
              position="absolute"
              {...(renderIconAlignment === 'left' ? { left: '4x' } : { right: '4x' })}
              top={iconTop}
              sx={{ transform: 'translateY(-50%)' }}
              width="6x"
            >
              {renderIcon({
                focused: inputFocused,
              })}
            </Box>
          )}

        {!!inputValue && inputFocused && (
          <ClearButton
            loading={loading}
            onClick={handleClear}
            inputRef={inputRef.current}
            blurInput={() => setInputFocused(false)}
          />
        )}

        {showSuggestions && (
          <LocationSuggestions
            inputId={inputId}
            inputValue={inputValue}
            suggestionsMenuAlignment={suggestionsMenuAlignment}
            floatingMenu={floatingMenu}
            inputFocused={inputFocused}
            activeIndex={activeIndex}
            options={options}
            handleSelectChange={handleSelectChange}
            setActiveIndex={setActiveIndex}
            includeHomeAddress={includeHomeAddress && !!formattedAddress}
            forceUseHomeAddress={forceUseHomeAddress}
            blurInput={() => setInputFocused(false)}
            enableTwoLineSuggestions={enableTwoLineSuggestions}
          />
        )}
      </Box>
    </>
  );
};

export default LocationInput;
